/**
 * ==========================================
 * MODERN COLOR PALETTE FOR UI COMPONENTS
 * ==========================================
 *
 * Predefined color palette for text, backgrounds, and other UI elements.
 * Designed to be modern, accessible, and consistent across the platform.
 *
 * Usage:
 * - Text blocks, MCQ blocks, Cover blocks: text/background colors
 * - Graph elements: function/point colors
 * - Any component needing color selection
 */

export interface ColorDefinition {
  id: string;
  name: string;
  hex: string;
  category: 'neutral' | 'pastel' | 'vibrant' | 'deep';
}

/**
 * SYSTEM COLORS - Neutrals and grays
 * Row 1 in the color picker
 */
export const SYSTEM_COLORS: ColorDefinition[] = [
  { id: 'black', name: 'Black', hex: '#000000', category: 'neutral' },
  { id: 'dark_gray', name: 'Dark Gray', hex: '#4B5563', category: 'neutral' },
  { id: 'gray', name: 'Gray', hex: '#9CA3AF', category: 'neutral' },
  { id: 'light_gray', name: 'Light Gray', hex: '#D1D5DB', category: 'neutral' },
  { id: 'pale_gray', name: 'Pale Gray', hex: '#E5E7EB', category: 'neutral' },
  { id: 'white', name: 'White', hex: '#FFFFFF', category: 'neutral' },
];

/**
 * PASTEL COLORS - Soft, gentle tones
 * Row 2 in the color picker
 */
export const PASTEL_COLORS: ColorDefinition[] = [
  { id: 'pale_yellow', name: 'Pale Yellow', hex: '#FEF3C7', category: 'pastel' },
  { id: 'peach', name: 'Peach', hex: '#FED7AA', category: 'pastel' },
  { id: 'pale_pink', name: 'Pale Pink', hex: '#FBCFE8', category: 'pastel' },
  { id: 'lavender', name: 'Lavender', hex: '#DDD6FE', category: 'pastel' },
  { id: 'pale_blue', name: 'Pale Blue', hex: '#BFDBFE', category: 'pastel' },
  { id: 'mint', name: 'Mint', hex: '#BBF7D0', category: 'pastel' },
];

/**
 * VIBRANT COLORS - Bright, energetic tones
 * Row 3 in the color picker
 */
export const VIBRANT_COLORS: ColorDefinition[] = [
  { id: 'yellow', name: 'Yellow', hex: '#FBBF24', category: 'vibrant' },
  { id: 'orange', name: 'Orange', hex: '#FB923C', category: 'vibrant' },
  { id: 'red', name: 'Red', hex: '#F87171', category: 'vibrant' },
  { id: 'purple', name: 'Purple', hex: '#C084FC', category: 'vibrant' },
  { id: 'blue', name: 'Blue', hex: '#60A5FA', category: 'vibrant' },
  { id: 'green', name: 'Green', hex: '#4ADE80', category: 'vibrant' },
];

/**
 * DEEP COLORS - Rich, saturated tones
 * Row 4 in the color picker
 */
export const DEEP_COLORS: ColorDefinition[] = [
  { id: 'olive', name: 'Olive', hex: '#854D0E', category: 'deep' },
  { id: 'brown', name: 'Brown', hex: '#92400E', category: 'deep' },
  { id: 'maroon', name: 'Maroon', hex: '#881337', category: 'deep' },
  { id: 'dark_purple', name: 'Dark Purple', hex: '#581C87', category: 'deep' },
  { id: 'navy', name: 'Navy', hex: '#1E3A8A', category: 'deep' },
  { id: 'forest', name: 'Forest Green', hex: '#14532D', category: 'deep' },
];

/**
 * ALL COLORS - Combined palette in display order
 */
export const COLOR_PALETTE: ColorDefinition[] = [
  ...SYSTEM_COLORS,
  ...PASTEL_COLORS,
  ...VIBRANT_COLORS,
  ...DEEP_COLORS,
];

/**
 * Helper: Get color definition by hex value
 */
export const getColorByHex = (hex: string): ColorDefinition | undefined => {
  return COLOR_PALETTE.find(color => color.hex.toLowerCase() === hex.toLowerCase());
};

/**
 * Helper: Get color name for display
 */
export const getColorName = (hex: string): string => {
  if (hex === 'transparent') return 'Transparent';
  const color = getColorByHex(hex);
  return color?.name || hex;
};

/**
 * Helper: Check if color is in predefined palette
 */
export const isCustomColor = (hex: string): boolean => {
  if (hex === 'transparent' || hex === 'theme') return false;
  return !COLOR_PALETTE.some(color => color.hex.toLowerCase() === hex.toLowerCase());
};

export default COLOR_PALETTE;
